import React from "react";
import { TopBar } from "@/components/TopBar";
import { ROLES, ROLE_LABELS } from "@/lib/roles";
import {
  apiListWorkflows,
  apiListPos,
  apiCreateWorkflow,
  apiUpdateWorkflow,
  formatApiErrorDetail,
} from "@/lib/api";
import { Loader2, Plus, Save, X, RefreshCw, Power, ArrowRight, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { namaPos, petaPos } from "@/lib/posNama";

const KATEGORI = ["BANNER", "DTF", "JERSEY", "CUSTOM"];
const newStep = () => ({ pos_code: "", role: "", alternatif: [] });
const EMPTY = { code: "", nama: "", kategori: "CUSTOM", is_configurable: true, is_active: true, steps: [] };

export default function WorkflowManagement() {
  const { user } = useAuth();
  const lihatSaja = user?.role === "manager";  // owner 2026-10-12: the Manager views, the owner changes
  const [rows, setRows] = React.useState([]);
  const [posList, setPosList] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [notice, setNotice] = React.useState(null);
  const [form, setForm] = React.useState(EMPTY);
  const [editingId, setEditingId] = React.useState(null);
  const [saving, setSaving] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [wf, pos] = await Promise.all([apiListWorkflows(), apiListPos()]);
      setRows(wf);
      setPosList(pos);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const flash = (m) => { setNotice(m); setTimeout(() => setNotice(null), 2500); };
  const resetForm = () => { setForm(EMPTY); setEditingId(null); };

  const addStep = () => setForm({ ...form, steps: [...form.steps, newStep()] });
  const removeStep = (i) => setForm({ ...form, steps: form.steps.filter((_, idx) => idx !== i) });
  const updateStep = (i, patch) => {
    const steps = form.steps.map((s, idx) => (idx === i ? { ...s, ...patch } : s));
    setForm({ ...form, steps });
  };

  // v2.18.1: an existing workflow that is not Configurable keeps its steps (the server refuses
  // them); a built-in one (DTF, Jersey, …) can never be made Configurable.
  const langkahTerkunci = !!editingId && !form.is_configurable;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    const payload = {
      code: form.code,
      nama: form.nama,
      kategori: form.kategori,
      is_configurable: form.is_configurable,
      is_active: form.is_active,
      ...(langkahTerkunci ? {} : { steps: form.steps.map((s, idx) => ({
        urutan: idx + 1,
        pos_code: s.pos_code,
        role: s.role || null,
        alternatif: s.alternatif || [],
      })) }),
    };
    try {
      if (editingId) { await apiUpdateWorkflow(editingId, payload); flash("Workflow diperbarui"); }
      else { await apiCreateWorkflow(payload); flash("Workflow ditambahkan"); }
      resetForm();
      await load();
    } catch (e2) {
      setError(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (w) => {
    setEditingId(w.id);
    setForm({
      code: w.code || "",
      nama: w.nama,
      kategori: w.kategori,
      is_configurable: w.is_configurable,
      alur_bawaan: !!w.alur_bawaan,
      is_active: w.is_active,
      steps: (w.steps || []).map((s) => ({ pos_code: s.pos_code, role: s.role || "", alternatif: s.alternatif || [] })),
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const toggleQc = async (w) => {
    setError(null);
    try {
      await apiUpdateWorkflow(w.id, { qc_enabled: !w.qc_enabled });
      flash(`QC ${w.kategori} ${w.qc_enabled ? "dimatikan" : "diaktifkan"} — berlaku untuk SPK baru; SPK yang sudah ada tidak berubah.`);
      await load();
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const toggleActive = async (w) => {
    setError(null);
    try { await apiUpdateWorkflow(w.id, { is_active: !w.is_active }); await load(); }
    catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const inputCls = "mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none focus:border-amber-500/60";
  const labelCls = "text-[13px] font-semibold text-slate-200";
  const posCodes = posList.map((p) => p.code);
  const peta = petaPos(posList);  // v2.17.2: the name first, the code only as a small note (this is the setup page)

  return (
    <div data-testid="workflow-management-page" className="min-h-screen bg-slate-950 text-slate-200">
      <TopBar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-100">Manajemen Workflow</h1>
          <p className="text-sm text-slate-400 mt-1">Alur produksi DB-driven. Setiap step mereferensikan pos produksi + role, mendukung alternatif (mis. DTF press satuan/roll).</p>
        </div>

        {notice && <div data-testid="wf-notice" className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-2.5 text-sm text-emerald-300">{notice}</div>}
        {error && <div data-testid="wf-error" className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-sm text-red-300">{error}</div>}

        {!lihatSaja && <form data-testid="wf-form" onSubmit={onSubmit} className="rounded-xl border border-slate-800 bg-slate-900/60 p-6 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className={labelCls}>Kode</label>
              <input data-testid="wf-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} required className={inputCls} placeholder="WF_MUG" />
            </div>
            <div>
              <label className={labelCls}>Nama</label>
              <input data-testid="wf-nama" value={form.nama} onChange={(e) => setForm({ ...form, nama: e.target.value })} required className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Kategori</label>
              <select data-testid="wf-kategori" value={form.kategori} onChange={(e) => setForm({ ...form, kategori: e.target.value })} className={inputCls}>
                {KATEGORI.map((k) => (<option key={k} value={k}>{k}</option>))}
              </select>
            </div>
            <div className="flex items-end gap-4">
              <label className={`inline-flex items-center gap-2 text-sm ${form.alur_bawaan ? "text-slate-500" : "text-slate-300"}`}><input data-testid="wf-configurable" type="checkbox" checked={form.is_configurable} disabled={form.alur_bawaan} onChange={(e) => setForm({ ...form, is_configurable: e.target.checked })} /> Configurable</label>
              <label className="inline-flex items-center gap-2 text-sm text-slate-300"><input data-testid="wf-active" type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} /> Aktif</label>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className={labelCls}>Urutan Step</span>
              {!langkahTerkunci && <button type="button" data-testid="wf-add-step" onClick={addStep} className="inline-flex items-center gap-1.5 rounded-md border border-slate-700 bg-slate-800/60 px-3 py-1.5 text-xs text-slate-300 hover:border-amber-500/40 hover:text-amber-300"><Plus size={13} /> Tambah Step</button>}
            </div>
            {langkahTerkunci && (
              <div data-testid="wf-langkah-terkunci" className="space-y-2">
                <p className="text-xs text-slate-400">{form.alur_bawaan
                  ? "Alur bawaan: langkahnya diatur aplikasi dan tidak bisa diubah di sini. QC diatur dengan tombol QC di daftar alur."
                  : "Langkah alur ini terkunci. Centang \"Configurable\" bila langkahnya memang boleh diubah."}</p>
                <div className="flex flex-wrap items-center gap-2">
                  {form.steps.map((s, i) => (
                    <span key={i} className="rounded border border-slate-700 px-2 py-1 text-xs text-slate-300">{i + 1}. {namaPos(s.pos_code, peta)}{(s.alternatif || []).length ? ` / ${s.alternatif.map((a) => namaPos(a, peta)).join(" / ")}` : ""}</span>
                  ))}
                </div>
              </div>
            )}
            {!langkahTerkunci && form.steps.length === 0 && <p className="text-xs text-slate-500">Belum ada step. Tambahkan step untuk menyusun alur.</p>}
            {!langkahTerkunci && form.steps.map((s, i) => (
              <div key={i} data-testid={`wf-step-${i}`} className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-end rounded-lg border border-slate-800 bg-slate-950/40 p-3">
                <div className="sm:col-span-1 text-center">
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-amber-500/15 text-amber-300 font-mono text-sm">{i + 1}</span>
                </div>
                <div className="sm:col-span-3">
                  <label className={labelCls}>Pos</label>
                  <select data-testid={`wf-step-pos-${i}`} value={s.pos_code} onChange={(e) => updateStep(i, { pos_code: e.target.value })} required className={inputCls}>
                    <option value="">— pilih pos —</option>
                    {posCodes.map((c) => (<option key={c} value={c}>{namaPos(c, peta)} ({c})</option>))}
                  </select>
                </div>
                <div className="sm:col-span-3">
                  <label className={labelCls}>Role</label>
                  <select data-testid={`wf-step-role-${i}`} value={s.role} onChange={(e) => updateStep(i, { role: e.target.value })} className={inputCls}>
                    <option value="">— (opsional) —</option>
                    {ROLES.map((r) => (<option key={r} value={r}>{ROLE_LABELS[r]}</option>))}
                  </select>
                </div>
                <div className="sm:col-span-4">
                  <label className={labelCls}>Alternatif Pos (opsional)</label>
                  <select data-testid={`wf-step-alt-${i}`} multiple value={s.alternatif} onChange={(e) => updateStep(i, { alternatif: Array.from(e.target.selectedOptions).map((o) => o.value) })} className={`${inputCls} h-[70px]`}>
                    {posCodes.filter((c) => c !== s.pos_code).map((c) => (<option key={c} value={c}>{namaPos(c, peta)} ({c})</option>))}
                  </select>
                </div>
                <div className="sm:col-span-1 flex justify-end">
                  <button type="button" data-testid={`wf-step-remove-${i}`} onClick={() => removeStep(i)} className="rounded-md border border-slate-700 bg-slate-800/60 p-2 text-slate-400 hover:border-red-500/40 hover:text-red-300"><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <div className="ml-auto flex items-center gap-2">
              {editingId && <button type="button" data-testid="wf-cancel" onClick={resetForm} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-700 px-4 py-2 text-sm text-slate-300 hover:text-slate-100"><X size={15} /> Batal</button>}
              <button data-testid="wf-submit" type="submit" disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-amber-500 px-5 py-2 text-sm font-semibold text-slate-950 hover:bg-amber-400 disabled:opacity-60">
                {saving ? <Loader2 className="animate-spin" size={16} /> : editingId ? <Save size={16} /> : <Plus size={16} />}
                {editingId ? "Simpan Workflow" : "Tambah Workflow"}
              </button>
            </div>
          </div>
        </form>}

        <div className="rounded-xl border border-slate-800 bg-slate-900/60 overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3 border-b border-slate-800">
            <h2 className="text-[15px] font-bold text-soq-biru-tua">Daftar Workflow</h2>
            <button data-testid="wf-refresh" onClick={load} className="inline-flex items-center gap-2 text-xs font-mono text-slate-400 hover:text-slate-200"><RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh</button>
          </div>
          {loading ? (
            <div className="p-8 flex items-center justify-center text-slate-400"><Loader2 className="animate-spin mr-2" size={18} /> Memuat…</div>
          ) : (
            <div className="divide-y divide-slate-800" data-testid="wf-list">
              {rows.map((w) => (
                <div key={w.id} data-testid={`wf-item-${w.kategori}`} className="p-5">
                  <div className="flex flex-wrap items-center gap-3 justify-between">
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-amber-300 text-sm">{w.code}</span>
                      <span className="text-slate-200 font-medium">{w.nama}</span>
                      <span className="text-[10px] font-mono px-2 py-0.5 rounded border border-cyan-500/30 bg-cyan-500/10 text-cyan-300">{w.kategori}</span>
                      {w.is_configurable && <span className="text-[10px] font-mono px-2 py-0.5 rounded border border-slate-600 text-slate-400">configurable</span>}
                      <span data-testid={`wf-status-${w.kategori}`} className={`text-[10px] font-mono px-2 py-0.5 rounded border ${w.is_active ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300" : "border-slate-600 bg-slate-700/30 text-slate-400"}`}>{w.is_active ? "Aktif" : "Nonaktif"}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {!lihatSaja && w.qc_enabled !== null && w.qc_enabled !== undefined && (
                        <button data-testid={`wf-qc-toggle-${w.kategori}`} onClick={() => toggleQc(w)} title="QC ditambahkan di akhir alur. Berlaku untuk SPK baru." className={`rounded-md border px-2.5 py-1.5 text-xs ${w.qc_enabled ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20" : "border-slate-700 bg-slate-800/60 text-slate-400 hover:text-slate-200"}`}>
                          {w.qc_enabled ? "QC: Aktif" : "QC: Nonaktif"}
                        </button>
                      )}
                      {!lihatSaja && <button data-testid={`wf-edit-${w.kategori}`} onClick={() => startEdit(w)} className="rounded-md border border-slate-700 bg-slate-800/60 px-2.5 py-1.5 text-xs text-slate-300 hover:border-amber-500/40 hover:text-amber-300">Edit</button>}
                      {!lihatSaja && <button data-testid={`wf-toggle-${w.kategori}`} onClick={() => toggleActive(w)} className="inline-flex items-center rounded-md border border-slate-700 bg-slate-800/60 px-2.5 py-1.5 text-xs text-slate-300 hover:border-red-500/40 hover:text-red-300"><Power size={13} /></button>}
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    {w.steps.length === 0 && <span className="text-xs text-slate-500">Belum ada step (configurable).</span>}
                    {w.steps.map((s, idx) => (
                      <React.Fragment key={idx}>
                        <span className="inline-flex flex-col items-center rounded-lg border border-slate-700 bg-slate-950/60 px-3 py-1.5">
                          <span data-testid={`wf-langkah-${w.kategori}-${idx}`} className="text-xs font-semibold text-slate-200">{namaPos(s.pos_code, peta)}</span>
                          <span className="font-mono text-[10px] text-slate-500">{s.pos_code}</span>
                          {s.alternatif?.length > 0 && (
                            <span className="text-[10px] text-amber-300">atau: {s.alternatif.map((c) => namaPos(c, peta)).join(" / ")}</span>
                          )}
                          {s.role && <span className="text-[10px] text-slate-500">{ROLE_LABELS[s.role] || s.role}</span>}
                        </span>
                        {idx < w.steps.length - 1 && <ArrowRight size={14} className="text-slate-600" />}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              ))}
              {rows.length === 0 && <div className="p-8 text-center text-slate-500">Belum ada workflow.</div>}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
